// src/components/ListPanel.tsx
import React, { useState } from 'react';
import { PanelWrapper } from './PanelWrapper';
import { EmptyState, LoadingState, ErrorState } from './StateComponents';

export interface ListRowData {
  id: string;
  icon?: React.ReactNode;
  meta: string;
  title: string;
  subtitle?: string;
  priority?: 'p0' | 'p1' | 'p2' | 'p3';
  status?: 'open' | 'closed' | 'pending';
  actions?: React.ReactNode;
  onClick?: () => void;
  expandable?: boolean;
  expandedContent?: React.ReactNode;
}

interface ListPanelProps {
  title: string;
  rows: ListRowData[];
  badge?: string;
  emptyState?: { icon?: string; title: string; body: string; action?: { label: string; onClick: () => void } };
  loading?: boolean;
  loadingText?: string;
  error?: { title: string; body: string; action?: { label: string; onClick: () => void } };
  maxHeight?: number;
  hierarchy?: 'primary' | 'secondary' | 'tertiary';
  demo?: boolean;
  className?: string;
}

export const ListPanel: React.FC<ListPanelProps> = ({
  title,
  rows,
  badge,
  emptyState,
  loading = false,
  loadingText = 'loading...',
  error,
  maxHeight = 400,
  hierarchy = 'secondary',
  demo = false,
  className = '',
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) {
    return (
      <PanelWrapper title={title} badge={badge} hierarchy={hierarchy} demo={demo} className={className}>
        <LoadingState text={loadingText} />
      </PanelWrapper>
    );
  }

  if (error) {
    return (
      <PanelWrapper title={title} badge={badge} hierarchy={hierarchy} demo={demo} className={className}>
        <ErrorState {...error} />
      </PanelWrapper>
    );
  }

  if (rows.length === 0 && emptyState) {
    return (
      <PanelWrapper title={title} badge={badge} hierarchy={hierarchy} demo={demo} className={className}>
        <EmptyState {...emptyState} />
      </PanelWrapper>
    );
  }

  return (
    <PanelWrapper title={title} badge={badge} hierarchy={hierarchy} demo={demo} className={className}>
      <div style={{ maxHeight, overflowY: 'auto' }}>
        {rows.map((row) => (
          <React.Fragment key={row.id}>
            <div
              className="list-row"
              onClick={() => {
                if (row.expandable) {
                  setExpandedId(expandedId === row.id ? null : row.id);
                }
                row.onClick?.();
              }}
            >
              {row.icon && <span>{row.icon}</span>}
              <span className="list-row__meta">{row.meta}</span>
              <span className="list-row__title">{row.title}</span>
              {row.actions && <div className="list-row__actions">{row.actions}</div>}
            </div>
            {row.expandable && expandedId === row.id && row.expandedContent && (
              <div
                style={{
                  padding: '12px 16px',
                  borderTop: '1px solid var(--border-soft)',
                  background: 'var(--bg-surface-solid)',
                  animation: 'fade-in-up 250ms var(--ease-enter)',
                }}
              >
                {row.expandedContent}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </PanelWrapper>
  );
};

export default ListPanel;
